import React from "react";
import { capitalizeFirstLetter } from "@/utils/helpers";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { IconButton, Tooltip, Button, Chip } from "@mui/material";
import { Delete, Edit, FastForward, Http, Terminal } from "@mui/icons-material";
import { useAtomValue } from "jotai";
import { videoPlayerAtom } from "@/atoms/playback-timestamp-atom";
import { type PreRequestResponse, type AssertionCondition } from "@/utils/types";
import { stackoverflowDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import SyntaxHighlighter from "react-syntax-highlighter";

const ReasoningAndCommands = ({
  title,
  absoluteTimestamp,
  relativeTimestamp,
  stepNumber,
  stepDescription,
  commands,
  reasoning,
  promptActionHandler,
  handleMoveToNetworkLog,
  handleMoveToDeviceLog,
  preRequestResponse,
  displayAnonymousTooltip = false,
  extraContent,
  fromCache,
  removeCacheHandler,
  isRemovingCache,
  shouldDisplayRemoveCacheButton = false,
  assertionConditions,
  assertionRetryNumber,
  isCheck = false,
}: {
  title?: string;
  stepNumber?: string;
  stepDescription?: string;
  fromCache?: boolean;
  reasoning?: string[];
  commands: string;
  absoluteTimestamp: number;
  relativeTimestamp?: number;
  promptActionHandler?: () => void;
  handleMoveToNetworkLog?: (timestamp: number) => void;
  handleMoveToDeviceLog?: (timestamp: number) => void;
  preRequestResponse?: PreRequestResponse;
  displayAnonymousTooltip?: boolean;
  extraContent?: React.ReactNode;
  removeCacheHandler?: VoidFunction;
  isRemovingCache?: boolean;
  shouldDisplayRemoveCacheButton: boolean;
  assertionConditions?: AssertionCondition[];
  assertionRetryNumber?: number;
  /**
   * True for a `check`, where a false condition was the answer the test
   * asked for rather than a failure. Reaches this screen as the identical
   * record an assert does, so only the SDK's own mode can say.
   */
  isCheck?: boolean;
}) => {
  const videoPlayer = useAtomValue(videoPlayerAtom);

  return (
    <ContentWrapper>
      <PropertyWrapper>
        <ActionHandlerWrapper>
          <p>{DateTime.fromMillis(absoluteTimestamp).toFormat("hh:mm:ss")}</p>
          {(promptActionHandler ?? displayAnonymousTooltip) && (
            <Tooltip
              title={`Refine step${
                displayAnonymousTooltip ? ". Logged in users only" : ""
              }`}
            >
              <span>
                <IconButton
                  aria-label="Edit prompt"
                  disabled={displayAnonymousTooltip}
                  onClick={promptActionHandler}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {handleMoveToNetworkLog && relativeTimestamp && (
            <Tooltip title={"Jump to network log"}>
              <IconButton
                aria-label="Jump to network log"
                onClick={() => handleMoveToNetworkLog(relativeTimestamp)}
              >
                <Http />
              </IconButton>
            </Tooltip>
          )}
          {handleMoveToDeviceLog && relativeTimestamp && (
            <Tooltip title={"Jump to device log"}>
              <IconButton
                aria-label="Jump to device log"
                onClick={() => handleMoveToDeviceLog(relativeTimestamp)}
              >
                <Terminal />
              </IconButton>
            </Tooltip>
          )}
          {videoPlayer != null && (
            <Tooltip title={"Watch video from this point"}>
              <IconButton
                aria-label="Watch video from this point"
                onClick={() => {
                  if (videoPlayer != null) {
                    videoPlayer.currentTime(relativeTimestamp / 1000);
                  }
                }}
              >
                <FastForward />
              </IconButton>
            </Tooltip>
          )}
        </ActionHandlerWrapper>
      </PropertyWrapper>
      {title != null && (
        <PropertyWrapper>
          <Title>Test</Title>
          <p>{title}</p>
        </PropertyWrapper>
      )}
      {stepNumber && <Title>Step number {stepNumber}</Title>}
      {assertionConditions && assertionConditions.length > 0 ? (
        <PropertyWrapper>
          <Title
            title={
              isCheck
                ? "A check, not an assertion: the test asked for these verdicts and decided for itself, so a condition that did not hold here did not fail the run"
                : undefined
            }
          >
            {isCheck ? "Check" : "Assertion"}
            {assertionRetryNumber ? ` - Retry ${assertionRetryNumber}` : ""}
          </Title>
          <AssertionList>
            {assertionConditions.map((item, index) => (
              <AssertionRow key={index}>
                <span>{item.condition}</span>
                <AssertionResultChip
                  label={item.result ? "True" : "False"}
                  size="small"
                  $passed={item.result}
                  $advisory={isCheck}
                />
              </AssertionRow>
            ))}
          </AssertionList>
        </PropertyWrapper>
      ) : (
        <>
          {stepDescription && (
            <PropertyWrapper>
              <Title>Prompt</Title>
              <p>{stepDescription}</p>
            </PropertyWrapper>
          )}
        </>
      )}
      {fromCache && (
        <div className={"flex gap-2.5 items-center"}>
          <Title>Action taken from cache</Title>
          {shouldDisplayRemoveCacheButton && (
            <Button
              startIcon={<Delete />}
              variant={"outlined"}
              color={"error"}
              loading={isRemovingCache}
              onClick={removeCacheHandler}
              disabled={!removeCacheHandler}
            >
              Remove from cache
            </Button>
          )}
        </div>
      )}
      {!(assertionConditions && assertionConditions.length > 0) && (
        <PropertyWrapper>
          <Title>Commands</Title>
          <CustomUl>
            {commands
              .split("\n")
              .map((value, index) => value && <li key={index}>{value}</li>)}
          </CustomUl>
        </PropertyWrapper>
      )}
      {!(assertionConditions && assertionConditions.length > 0) &&
        (reasoning ?? []).length > 0 && (
          <PropertyWrapper>
            <Title>Reasoning</Title>
            <CustomUl>
              {reasoning!.map((item, index) => (
                <li key={index}>{capitalizeFirstLetter(item.trim())}</li>
              ))}
            </CustomUl>
          </PropertyWrapper>
        )}
      {preRequestResponse != null && (
        <PropertyWrapper>
          <Title>Pre-request response</Title>
          {preRequestResponse?.status && (
            <CustomUl>
              <li
                className={"text-l"}
              >{`Status code ${preRequestResponse.status}`}</li>
            </CustomUl>
          )}
          <SyntaxHighlighter
            language="json"
            style={stackoverflowDark}
            customStyle={{}}
          >
            {JSON.stringify(preRequestResponse, null, 1)}
          </SyntaxHighlighter>
        </PropertyWrapper>
      )}
      {extraContent}
    </ContentWrapper>
  );
};

const CustomUl = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style-type: disc;
  unicode-bidi: isolate;
  padding-inline-start: 16px;
`;

const AssertionList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const AssertionRow = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.875rem;
`;

// A condition that did not hold is coloured by what it meant to the run:
// red when it broke the test, amber when the test read it and carried on.
const AssertionResultChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "$passed" && prop !== "$advisory",
})<{ $passed: boolean; $advisory?: boolean }>`
  && {
    font-weight: 600;
    color: ${({ $passed, $advisory }) =>
      $passed ? "#1e7e34" : $advisory ? "#8a5300" : "#c62828"};
    background-color: ${({ $passed, $advisory }) =>
      $passed
        ? "rgba(30, 126, 52, 0.12)"
        : $advisory
          ? "rgba(138, 83, 0, 0.12)"
          : "rgba(198, 40, 40, 0.12)"};
  }
`;

const PropertyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 1rem;
  ul {
    font-size: 0.875rem;
  }
`;

const ActionHandlerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const Title = styled.p`
  font-weight: 550;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export default ReasoningAndCommands;
